import { AxiosInstance, AxiosResponse } from 'axios';
import { axiosInstance } from '../axios';

export const INCOME_STATEMENT_PRICE = 100;
export const BALANCE_SHEET_PRICE = 100;
export const VALUATION_PRICE = 45000;

export type FinancialReportType = 'IncomeStatement' | 'BalanceSheet';

export type FinancialReportStatus =
    | 'Pending'
    | 'Completed'
    | 'Failed'
    | 'Refunded';

export interface PurchaseFinancialReportResponse {
    reportId: number;
    type: FinancialReportType;
    status: FinancialReportStatus;
    creditsSpent: number;
    remainingUserCredits: number;
}

export interface FinancialReportPayload {
    type?: string;
    fiscalYear?: number;
    currency?: string;
    accountingStandard?: string;
    company?: {
        name?: string;
        jurisdiction?: string;
        nationalCompanyNumber?: string;
    };
    provenance?: {
        sourceName?: string;
        sourceTrustTier?: string;
        accessMethod?: string;
        retrievalTimestamp?: string;
        confidenceScore?: number;
        originalDocumentName?: string;
        selectedFilingYear?: number;
    };
    incomeStatement?: Record<string, number | null | undefined>;
    assets?: Record<string, number | null | undefined>;
    liabilities?: Record<string, number | null | undefined>;
    equity?: Record<string, number | null | undefined>;
    adjustments?: string[];
}

export interface FinancialReport {
    id: number;
    companyId: number;
    type: FinancialReportType;
    status: FinancialReportStatus;
    creditsSpent: number;
    createdAt: number;
    completedAt: number | null;
    dataJson: string | null;
    errorMessage: string | null;
    externalJobId: string | null;
}

class FinancialReportService {
    private axios: AxiosInstance;

    constructor() {
        this.axios = axiosInstance;
    }

    async purchaseIncomeStatement(companyId: number): Promise<PurchaseFinancialReportResponse> {
        const response = await this.axios.post(
            `/api/companies/${companyId}/financial-reports/income-statement`,
            { creditCountToSpend: INCOME_STATEMENT_PRICE },
        );
        return response.data;
    }

    async purchaseBalanceSheet(companyId: number): Promise<PurchaseFinancialReportResponse> {
        const response = await this.axios.post(
            `/api/companies/${companyId}/financial-reports/balance-sheet`,
            { creditCountToSpend: BALANCE_SHEET_PRICE },
        );
        return response.data;
    }

    async getReport(reportId: number): Promise<AxiosResponse<FinancialReport>> {
        return await this.axios.get(`/api/financial-reports/${reportId}`).catch((err) => err.response);
    }

    parseReportPayload(report: FinancialReport): FinancialReportPayload | null {
        if (!report.dataJson) return null;
        try {
            return JSON.parse(report.dataJson) as FinancialReportPayload;
        } catch {
            return null;
        }
    }
}

export const financialReportService = new FinancialReportService();
